import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TardiosProvider } from './context/TardiosContext';
import { AlertasVeiculosProvider } from './context/AlertasVeiculosContext';
import { AvisoVencimentosProvider } from './context/AvisoVencimentosContext';
import { NotificacoesProvider } from './context/NotificacoesContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Obras from './pages/Obras';
import NovaObra from './pages/NovaObra';
import ObraDetalhes from './pages/ObraDetalhes';
import Equipamentos from './pages/Equipamentos';
import ProdutosQuimicos from './pages/ProdutosQuimicos';
import Planeamento from './pages/Planeamento';
import Maquetes from './pages/MaquetesSimple';
import Veiculos from './pages/Veiculos';
import Utilizadores from './pages/Utilizadores';
import Despesas from './pages/Despesas';
import MobileDashboard from './pages/MobileDashboard';
import VerificacaoAndaime from './pages/VerificacaoAndaime';
import FichaManutencao from './pages/FichaManutencao';
import Manutencoes from './pages/Manutencoes';
import Configuracoes from './pages/Configuracoes';
import RecursosHumanos from './pages/RecursosHumanos';
import Revolut from './pages/Revolut';
import RevolutCallback from './pages/RevolutCallback';
import Reconciliador from './pages/Reconciliador';
import HistoricoAndaimes from './pages/HistoricoAndaimes';
import FichaLevantamento from './pages/FichaLevantamento';
import RegistoHoras from './pages/RegistoHoras';
import RegistoIncidente from './pages/RegistoIncidente';
import EquipamentosMobile from './pages/EquipamentosMobile';
import AdminFichas from './pages/AdminFichas';
import Relatorios from './pages/Relatorios';
import Orcamentos from './pages/Orcamentos';
import OrcamentoDetalhe from './pages/OrcamentoDetalhe';
import GestaoFinanceira from './pages/GestaoFinanceira';
import ProdutosQuimicosMobile from './pages/ProdutosQuimicosMobile';
import ContactosUteis from './pages/ContactosUteis';
import ManuaisMobile from './pages/ManuaisMobile';
import DocumentosMobile from './pages/DocumentosMobile';
import FichaProcedimentosSeguranca from './pages/FichaProcedimentosSeguranca';
import OnboardingTrabalhador from './pages/OnboardingTrabalhador';
import Lixeira from './pages/Lixeira';
import SistemaIntegrado from './pages/SistemaIntegrado';
import Notificacoes from './pages/Notificacoes';
import ConsultaPublica from './pages/ConsultaPublica';
import EquipamentoPublico from './pages/EquipamentoPublico';
import SubcontratadoPortal from './pages/SubcontratadoPortal';
import AcordoFornecedorPublico from './pages/AcordoFornecedorPublico';
import Layout from './components/Layout';
import './App.css';

const isMobileDevice = () => {
  const ua = navigator.userAgent || navigator.vendor || '';
  const mobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const smallScreen = window.innerWidth <= 768;
  return mobileUA || smallScreen;
};

// Mapeamento de paths para chaves de permissão
const ROUTE_PERMISSIONS = {
  '/obras': 'obras',
  '/obras/nova': 'obras',
  '/trabalhadores': 'trabalhadores',
  '/recursos-humanos': 'recursos-humanos',
  '/equipamentos': 'equipamentos',
  '/veiculos': 'veiculos',
  '/planeamento': 'planeamento',
  '/manutencoes': 'manutencoes',
  '/maquetes': 'maquetes',
  '/orcamentos': 'orcamentos',
  '/gestao-financeira': 'gestao-financeira',
  '/despesas': 'despesas',
  '/relatorios': 'relatorios',
  '/sistema-integrado': 'sistema-integrado',
  '/notificacoes': 'notificacoes',
};

const TrabalhadoresRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('separador', 'trabalhadores');
  return <Navigate to={{ pathname: '/recursos-humanos', search: '?' + params.toString(), hash: location.hash }} state={location.state} replace />;
};

// Componente para rotas protegidas (com Layout desktop)
const ProtectedRoute = ({ children, permissionKey, permissionAny, adminOnly }) => {
  const { isAuthenticated, loading, hasPermission, user } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if ((permissionAny && !permissionAny.some(hasPermission)) || (permissionKey && !hasPermission(permissionKey)) || (adminOnly && user?.role !== 'admin')) {
    return <Layout>
      <div style={{ padding: '60px 30px', textAlign: 'center' }}>
        <h2 style={{ color: '#e53e3e', marginBottom: '10px' }}>Acesso Restrito</h2>
        <p style={{ color: '#718096' }}>Não tem permissão para aceder a este módulo. Contacte o administrador.</p>
      </div>
    </Layout>;
  }

  return <Layout>{children}</Layout>;
};

// Rotas mobile: sem Layout, sem sidebar, fullscreen
const MobileRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Redirect mobile users (by profile flag or device detection) to mobile dashboard
// Destinos possíveis de quem não tem o Dashboard, pela ordem do menu.
const DESTINOS_SEM_DASHBOARD = [
  ['despesas', '/despesas'],
  ['gestao-financeira', '/gestao-financeira'],
  ['obras', '/obras'],
  ['recursos-humanos', '/recursos-humanos'],
  ['trabalhadores', '/recursos-humanos?separador=trabalhadores'],
  ['orcamentos', '/orcamentos'],
  ['planeamento', '/planeamento'],
  ['manutencoes', '/manutencoes'],
  ['equipamentos', '/equipamentos'],
  ['veiculos', '/veiculos'],
  ['maquetes', '/maquetes'],
  ['relatorios', '/relatorios'],
  ['sistema-integrado', '/sistema-integrado'],
  ['notificacoes', '/notificacoes'],
];

const DashboardRedirect = () => {
  const { user, hasPermission } = useAuth();
  if (user?.perfil_mobile || isMobileDevice()) {
    return <Navigate to="/mobile-dashboard" replace />;
  }
  // Sem a permissão do Dashboard, a entrada é o primeiro módulo a que tem acesso:
  // antes, quem só tinha Cartões via na mesma o resumo da empresa ao entrar.
  if (!hasPermission('dashboard')) {
    const destino = DESTINOS_SEM_DASHBOARD.find(([chave]) => hasPermission(chave));
    if (destino) return <Navigate to={destino[1]} replace />;
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
        <h2 style={{ color: '#2d3748' }}>Sem módulos atribuídos</h2>
        <p>A sua conta não tem acesso a nenhum módulo. Contacte o administrador.</p>
      </div>
    );
  }
  return <Dashboard />;
};

// Placeholder pages for future implementation
const PlaceholderPage = ({ title, icon }) => (
  <div style={{ padding: '30px' }}>
    <h1>{icon} {title}</h1>
    <p style={{ color: '#666' }}>Esta página está em desenvolvimento.</p>
  </div>
);

function InternalApp() {
  return (
    <AuthProvider>
      <TardiosProvider>
      <AlertasVeiculosProvider>
      <AvisoVencimentosProvider>
      <NotificacoesProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/onboarding/:token" element={<OnboardingTrabalhador />} />
          <Route path="/consulta/:token" element={<ConsultaPublica />} />
          <Route path="/eq/:token" element={<EquipamentoPublico />} />
          <Route path="/subcontratado/:token" element={<SubcontratadoPortal />} />
          <Route path="/acordo-fornecedor/:token" element={<AcordoFornecedorPublico />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/obras"
            element={
              <ProtectedRoute permissionKey="obras">
                <Obras />
              </ProtectedRoute>
            }
          />
          <Route
            path="/obras/nova"
            element={
              <ProtectedRoute permissionKey="obras">
                <NovaObra />
              </ProtectedRoute>
            }
          />
          <Route
            path="/obras/:id"
            element={
              <ProtectedRoute permissionKey="obras">
                <ObraDetalhes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trabalhadores"
            element={
              <ProtectedRoute permissionKey="trabalhadores">
                <TrabalhadoresRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipamentos/produtos-quimicos"
            element={
              <ProtectedRoute permissionKey="equipamentos">
                <ProdutosQuimicos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipamentos"
            element={
              <ProtectedRoute permissionKey="equipamentos">
                <Equipamentos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/veiculos"
            element={
              <ProtectedRoute permissionKey="veiculos">
                <Veiculos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planeamento"
            element={
              <ProtectedRoute permissionKey="planeamento">
                <Planeamento />
              </ProtectedRoute>
            }
          />
          <Route
            path="/maquetes"
            element={
              <ProtectedRoute permissionKey="maquetes">
                <Maquetes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/maquetes/:obraId"
            element={
              <ProtectedRoute permissionKey="maquetes">
                <Maquetes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/utilizadores"
            element={
              <ProtectedRoute adminOnly>
                <Utilizadores />
              </ProtectedRoute>
            }
          />

          <Route
            path="/despesas"
            element={
              <ProtectedRoute permissionKey="despesas">
                <Despesas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile-dashboard"
            element={
              <MobileRoute>
                <MobileDashboard />
              </MobileRoute>
            }
          />
          <Route
            path="/andaime-verificacao"
            element={
              <MobileRoute>
                <VerificacaoAndaime />
              </MobileRoute>
            }
          />
          <Route
            path="/ficha-manutencao"
            element={
              <MobileRoute>
                <FichaManutencao />
              </MobileRoute>
            }
          />
          <Route
            path="/ficha-levantamento"
            element={
              <MobileRoute>
                <FichaLevantamento />
              </MobileRoute>
            }
          />
          <Route
            path="/registo-horas"
            element={
              <MobileRoute>
                <RegistoHoras />
              </MobileRoute>
            }
          />
          <Route
            path="/registo-incidente"
            element={
              <MobileRoute>
                <RegistoIncidente />
              </MobileRoute>
            }
          />
          <Route
            path="/equipamentos-mobile"
            element={
              <MobileRoute>
                <EquipamentosMobile />
              </MobileRoute>
            }
          />
          <Route
            path="/admin-fichas"
            element={
              <ProtectedRoute adminOnly>
                <AdminFichas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/andaimes-historico"
            element={
              <MobileRoute>
                <HistoricoAndaimes />
              </MobileRoute>
            }
          />
          <Route
            path="/relatorios"
            element={
              <ProtectedRoute permissionKey="relatorios">
                <Relatorios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orcamentos"
            element={
              <ProtectedRoute permissionKey="orcamentos">
                <Orcamentos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orcamentos/:id"
            element={
              <ProtectedRoute permissionKey="orcamentos">
                <OrcamentoDetalhe />
              </ProtectedRoute>
            }
          />

          <Route
            path="/gestao-financeira"
            element={
              <ProtectedRoute permissionKey="gestao-financeira">
                <GestaoFinanceira />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manutencoes"
            element={
              <ProtectedRoute permissionKey="manutencoes">
                <Manutencoes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/configuracoes"
            element={
              <ProtectedRoute adminOnly>
                <Configuracoes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/recursos-humanos"
            element={
              <ProtectedRoute permissionAny={["trabalhadores", "recursos-humanos"]}>
                <RecursosHumanos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/revolut"
            element={
              <ProtectedRoute permissionKey="despesas">
                <Revolut />
              </ProtectedRoute>
            }
          />
          <Route path="/revolut-callback" element={<RevolutCallback />} />

          <Route
            path="/reconciliacao"
            element={
              <ProtectedRoute permissionKey="despesas">
                <Reconciliador />
              </ProtectedRoute>
            }
          />

          <Route
            path="/produtos-quimicos"
            element={
              <MobileRoute>
                <ProdutosQuimicosMobile />
              </MobileRoute>
            }
          />
          {/* A antiga página das fichas técnicas passou a ser a dos produtos químicos */}
          <Route path="/fichas-tecnicas" element={<Navigate to="/produtos-quimicos" replace />} />

          <Route
            path="/contactos-uteis"
            element={
              <MobileRoute>
                <ContactosUteis />
              </MobileRoute>
            }
          />

          <Route
            path="/manuais"
            element={
              <MobileRoute>
                <ManuaisMobile />
              </MobileRoute>
            }
          />

          <Route
            path="/documentos-mobile"
            element={
              <MobileRoute>
                <DocumentosMobile />
              </MobileRoute>
            }
          />

          <Route
            path="/ficha-procedimentos-seguranca"
            element={
              <MobileRoute>
                <FichaProcedimentosSeguranca />
              </MobileRoute>
            }
          />

          <Route
            path="/lixeira"
            element={
              <ProtectedRoute adminOnly>
                <Lixeira />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sistema-integrado"
            element={
              <ProtectedRoute permissionKey="sistema-integrado">
                <SistemaIntegrado />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notificacoes"
            element={
              <ProtectedRoute permissionKey="notificacoes">
                <Notificacoes />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </NotificacoesProvider>
      </AvisoVencimentosProvider>
      </AlertasVeiculosProvider>
      </TardiosProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* O portal não monta os fornecedores que consultam APIs internas. */}
        <Route path="/manutencao/externo/:token" element={<AuthProvider><FichaManutencao acessoExterno /></AuthProvider>} />
        <Route path="*" element={<InternalApp />} />
      </Routes>
    </Router>
  );
}

export default App;
